import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { type ConditionProperties } from 'json-rules-engine';
import { SSwitch } from '@chargebee/sting-react';
import {
  nullishOperators,
  type AudienceFact,
  type NullishOperator,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/edit-rule-types';
import { ListOfValueSelector } from '@bbkAdminComponents/experiences/audiences/edit-rule/ListOfValueSelector';
import TextInput from '@bbkAdminComponents/inputs/text/text-input';
import {
  useDisableDemoReports,
  useShowDemoData,
} from '@bbkAdminComponents/play/use-show-demo-data';
import {
  GenericSelect,
  InputSelect,
  InputSelectWithOptions,
} from '@bbkAdminComponents/select/select';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { AppFieldType } from '@bbkAdminRedux/data-management/reducers';
import {
  AI_RESERVED_CHURN_RISK_LEVEL,
  AI_RULE_RESERVED_KEY_PREFIX,
  OFFER_RESERVED_FIELDS,
  RETENTION_OFFER_RESERVED_FIELD_NAME,
} from '@bbkAdminRedux/rtkq/integrations.slice';
import { useSelectAllOffers } from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { useGetProductLicense } from '@bbkAdminUtils/plans';

const allowCsvValuesFor = 'standard.Billing ID';

type InputRendererProps = {
  condition: ConditionProperties;
  fieldType?: AppFieldType;
  operator: string;
  fieldWithPrefix?: AudienceFact;
  onChange: (updates: Partial<Pick<ConditionProperties, 'value'>>) => void;
};

const parsePositiveFloatWithLimit = (
  value: string,
  decimalPlaces: number
): number | undefined => {
  if (value.trim() === '') {
    return undefined;
  }
  const parsedValue = parseFloat(value);

  if (
    Number.isNaN(parsedValue) ||
    parsedValue <= 0 ||
    !Number.isFinite(parsedValue)
  ) {
    console.error('Invalid number format or not a positive value');
    return undefined;
  }
  const result = parsedValue.toFixed(decimalPlaces);
  // + converts string to number, here string result to number
  return +result;
};

const predictiveChurnRiskLevels = [
  { label: 'High', value: 'High' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Low', value: 'Low' },
];

export const ConditionValueInputRenderer: React.FC<InputRendererProps> = ({
  condition,
  operator,
  fieldWithPrefix,
  onChange,
}) => {
  const appKey = useSelector(selectAppId);
  const allOffers = useSelectAllOffers({ appKey });
  const offerIds = useMemo(
    () => allOffers.map((x) => ({ value: x.id, label: x.display_name })),
    [allOffers]
  );

  const { agsLicense } = useGetProductLicense();
  const [disableDemoReports] = useDisableDemoReports();
  const isDemoData = useShowDemoData({ disableDemoReports });
  const showAiReservedFields = useFeatureFlags(FeatureFlagsE.ENABLE_PREDICTIVE);
  const shouldShowPredictiveAudienceOptions =
    showAiReservedFields || (agsLicense && isDemoData);

  const fieldType = fieldWithPrefix?.type;
  const isLovType =
    fieldType === AppFieldType.text &&
    (operator === '==' ||
      operator === '!=' ||
      operator === 'String.in' ||
      operator === 'String.notIn');

  const isEqualityOperators = operator === '==' || operator === '!=';

  const offerIdReservedField = OFFER_RESERVED_FIELDS.find(
    (x) => x.name === RETENTION_OFFER_RESERVED_FIELD_NAME
  );

  switch (true) {
    case nullishOperators.includes(operator as NullishOperator):
      return null;
    case operator === 'String.in':
    case operator === 'String.notIn': {
      let value = condition.value;

      if (!condition.value) {
        value = [];
      }
      if (!Array.isArray(value)) {
        value = [condition.value];
      }

      if (condition.fact === `reserved.${offerIdReservedField?.val}`) {
        return (
          <InputSelectWithOptions
            options={offerIds}
            value={value}
            onChange={(e) => onChange({ value: e })}
          />
        );
      }
      if (
        condition.fact ===
          `${AI_RULE_RESERVED_KEY_PREFIX}${AI_RESERVED_CHURN_RISK_LEVEL}` &&
        shouldShowPredictiveAudienceOptions
      ) {
        return (
          <InputSelectWithOptions
            options={predictiveChurnRiskLevels}
            value={value}
            onChange={(e) => onChange({ value: e })}
          />
        );
      }

      if (
        fieldWithPrefix?.field_name &&
        fieldWithPrefix.integration_id &&
        fieldWithPrefix?.is_lov
      ) {
        return (
          <ListOfValueSelector
            key={`${fieldWithPrefix.integration_id}-${fieldWithPrefix.field_name}`}
            isMulti
            integration_id={fieldWithPrefix.integration_id}
            fieldName={fieldWithPrefix.field_name}
            value={value}
            onChange={(e) => onChange({ value: e })}
          />
        );
      }
      return (
        <InputSelect
          value={value}
          onChange={(e) => onChange({ value: e })}
          allowCsvValues={allowCsvValuesFor === condition.fact}
        />
      );
    }
    case fieldType === AppFieldType.integer:
      return (
        <TextInput
          type="number"
          onChange={(e) => onChange({ value: parseInt(e.target.value, 10) })}
          value={condition.value}
        />
      );
    case fieldType === AppFieldType.decimal:
      return (
        <TextInput
          type="number"
          onChange={(e) =>
            onChange({
              value: parsePositiveFloatWithLimit(e.target.value, 4),
            })
          }
          value={condition.value !== undefined ? condition.value : ''}
        />
      );

    case operator === 'Date.isBefore':
    case operator === 'Date.isAfter':
    case operator === 'Date.willHappenWithinDays':
    case operator === 'Date.didHappenWithinDays':
      return (
        <div>
          <TextInput
            type="number"
            onChange={(e) => onChange({ value: parseInt(e.target.value, 10) })}
            placeholder="number of days e.g. 30"
            value={condition.value}
          />
          <span className="tw-mt-1 tw-inline-block tw-text-sm tw-font-medium">
            days of the session start date
          </span>
        </div>
      );
    case fieldType === AppFieldType.boolean:
      return (
        <SSwitch
          label={condition.value ? 'true' : 'false'}
          checked={condition.value}
          onCheckedChange={(checked) => onChange({ value: checked })}
        />
      );
    case fieldType === AppFieldType.date:
      return (
        <TextInput
          type="date"
          onChange={(e) => onChange({ value: e.target.value })}
          value={condition.value}
        />
      );
    case fieldType === AppFieldType.text:
    case fieldType === AppFieldType.email:
    case fieldType === AppFieldType.url:
    default:
      if (
        isLovType &&
        fieldWithPrefix?.field_name &&
        fieldWithPrefix.integration_id &&
        fieldWithPrefix?.is_lov
      ) {
        return (
          <ListOfValueSelector
            key={`${fieldWithPrefix.integration_id}-${fieldWithPrefix.field_name}`}
            integration_id={fieldWithPrefix.integration_id}
            fieldName={fieldWithPrefix.field_name}
            value={condition.value}
            onChange={(e) => onChange({ value: e })}
          />
        );
      }
      if (
        isEqualityOperators &&
        condition.fact ===
          `${AI_RULE_RESERVED_KEY_PREFIX}${AI_RESERVED_CHURN_RISK_LEVEL}`
      ) {
        return (
          <GenericSelect
            options={predictiveChurnRiskLevels}
            value={predictiveChurnRiskLevels.find(
              (level) => level.value === condition.value
            )}
            onChange={(e) => onChange({ value: e.value })}
          />
        );
      }

      return (
        <TextInput
          onChange={(e) => onChange({ value: e.target.value })}
          value={condition.value}
        />
      );
  }
};
